import { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PixelText } from '../ui/PixelText';
import { PixelButton } from '../ui/PixelButton';
import { PixelPanel } from '../ui/PixelPanel';
import { usePlayerStore } from '../store/player';
import { content, displayName, useDisplayLocale, type BundledSkill } from '../content/bundle';
import { api } from '../api/client';

interface LoadedLoadout {
  passiveId: string;
  activeIds: [string, string];
  ultimateId: string;
}

function SkillSlot({
  label,
  currentId,
  options,
  onChange
}: {
  label: string;
  currentId: string;
  options: BundledSkill[];
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = content.getSkill(currentId);

  return (
    <div className="relative">
      <button
        className="w-full text-left border-2 border-ink-600 p-2 hover:border-neon-cyan transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <PixelText size="sm" color="yellow">{label}</PixelText>
        <div className="mt-1">
          <PixelText size="sm" color="cyan">{current?.flavorName ?? currentId}</PixelText>
          {current && (
            <PixelText size="sm" color="white" className="opacity-70 block text-xs">
              {current.flavorText}
            </PixelText>
          )}
        </div>
      </button>
      {open && (
        <div className="absolute z-10 left-0 top-full w-full bg-ink-900 border-2 border-neon-cyan max-h-48 overflow-y-auto">
          {options.map(sk => (
            <button
              key={sk.id}
              className="w-full text-left px-3 py-2 hover:bg-ink-700 border-b border-ink-700"
              onClick={() => { onChange(sk.id); setOpen(false); }}
            >
              <PixelText size="sm" color="pink">{sk.flavorName}</PixelText>
              <PixelText size="sm" color="white" className="block text-xs opacity-70">{sk.flavorText}</PixelText>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function MemberDetail() {
  const { t } = useTranslation();
  const { memberId } = useParams<{ memberId: string }>();
  const me = usePlayerStore(s => s.me);
  const locale = useDisplayLocale();

  const member = memberId ? content.getMember(memberId) : undefined;
  const group = member ? content.getGroup(member.groupId) : undefined;

  const rosterEntry = (me?.roster as Array<{
    memberId: string;
    level: number;
    equipped?: { passiveId: string; activeIds: string[]; ultimateId: string };
  }>)?.find(r => r.memberId === memberId);

  const memberSkills = memberId ? content.getSkillsForMember(memberId) : [];
  const defaultPassive = memberSkills.find(s => s.type === 'passive');
  const defaultActives = memberSkills.filter(s => s.type === 'active');
  const defaultUlt = memberSkills.find(s => s.type === 'ultimate');

  const defaultLoadout: LoadedLoadout = {
    passiveId: defaultPassive?.id ?? '',
    activeIds: [defaultActives[0]?.id ?? '', defaultActives[1]?.id ?? ''],
    ultimateId: defaultUlt?.id ?? '',
  };

  const savedLoadout = rosterEntry?.equipped
    ? {
        passiveId: rosterEntry.equipped.passiveId,
        activeIds: rosterEntry.equipped.activeIds as [string, string],
        ultimateId: rosterEntry.equipped.ultimateId
      }
    : defaultLoadout;

  const [loadout, setLoadout] = useState<LoadedLoadout>(savedLoadout);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  if (!me) return <Navigate to="/login" replace />;
  if (!memberId) return <Navigate to="/roster" replace />;

  const passiveSkills = memberSkills.filter(s => s.type === 'passive');
  const activeSkills = memberSkills.filter(s => s.type === 'active');
  const ultimateSkills = memberSkills.filter(s => s.type === 'ultimate');

  const isValid =
    loadout.passiveId &&
    loadout.activeIds[0] &&
    loadout.activeIds[1] &&
    loadout.activeIds[0] !== loadout.activeIds[1] &&
    loadout.ultimateId;

  const handleSave = async () => {
    if (!isValid || !memberId) return;
    setSaving(true);
    setSaveMsg('');
    try {
      await api.me.updateLoadout(memberId, {
        passiveId: loadout.passiveId,
        activeIds: loadout.activeIds,
        ultimateId: loadout.ultimateId
      });
      setSaveMsg(t('member_detail.saved'));
    } catch (e) {
      setSaveMsg(`Error: ${String(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const memberName = member ? displayName(member, locale) : memberId;
  const groupName = group?.name[locale] ?? member?.groupId ?? '';

  return (
    <main className="min-h-screen flex flex-col gap-4 p-4">
      <div className="flex items-center gap-3">
        <Link to="/roster"><PixelButton variant="ghost" className="text-xs">{t('member_detail.back')}</PixelButton></Link>
        <PixelText size="xl" color="pink">
          {memberName}
        </PixelText>
      </div>

      {/* Member info */}
      {member ? (
        <PixelPanel className="flex gap-4 items-center">
          <img
            src={`/stanwars/sprites/members/${memberId}/portrait.png`}
            alt={memberName}
            className="w-16 h-16 flex-shrink-0 block"
            style={{ imageRendering: 'pixelated', objectFit: 'contain', backgroundColor: group?.colorPrimary ?? '#444' }}
          />
          <div>
            <PixelText size="lg" color="cyan">{memberName}</PixelText>
            <PixelText size="sm" color="yellow" className="block">
              {groupName} · {member.role} · {member.rarity}
            </PixelText>
            <PixelText size="sm" color="white" className="block opacity-70">
              {t('roster.level', { lv: rosterEntry?.level ?? 1 })}
            </PixelText>
          </div>
        </PixelPanel>
      ) : (
        <PixelPanel>
          <PixelText color="yellow">{t('member_detail.unknown_member', { id: memberId })}</PixelText>
        </PixelPanel>
      )}

      {/* Loadout editor */}
      <PixelPanel>
        <PixelText size="base" color="pink" className="mb-3 block">{t('member_detail.loadout')}</PixelText>
        <div className="flex flex-col gap-3">
          <SkillSlot
            label={t('member_detail.passive')}
            currentId={loadout.passiveId}
            options={passiveSkills}
            onChange={id => setLoadout(l => ({ ...l, passiveId: id }))}
          />
          <SkillSlot
            label={t('member_detail.active1')}
            currentId={loadout.activeIds[0]}
            options={activeSkills}
            onChange={id => setLoadout(l => ({ ...l, activeIds: [id, l.activeIds[1]] }))}
          />
          <SkillSlot
            label={t('member_detail.active2')}
            currentId={loadout.activeIds[1]}
            options={activeSkills}
            onChange={id => setLoadout(l => ({ ...l, activeIds: [l.activeIds[0], id] }))}
          />
          <SkillSlot
            label={t('member_detail.ultimate')}
            currentId={loadout.ultimateId}
            options={ultimateSkills}
            onChange={id => setLoadout(l => ({ ...l, ultimateId: id }))}
          />
        </div>

        {!isValid && (
          <PixelText size="sm" color="yellow" className="mt-2 block">
            {t('member_detail.incomplete_loadout')}
          </PixelText>
        )}

        <div className="mt-4 flex items-center gap-3">
          <PixelButton onClick={handleSave} disabled={!isValid || saving}>
            {saving ? t('member_detail.saving') : t('member_detail.save')}
          </PixelButton>
          {saveMsg && <PixelText size="sm" color="green">{saveMsg}</PixelText>}
        </div>
      </PixelPanel>
    </main>
  );
}
